import { clsx } from 'clsx'
import { cloneElement, type ForwardedRef, isValidElement, type ReactNode, type ReactElement } from 'react'
import { View } from 'react-native'

import { mapPropsVariants } from '@/src/general/utils/mapPropsVariants'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { Typography } from '../text/Typography'
import { bulletVariants, type TBulletSlots, type TBulletVariantProps } from './bulletVariants'

export type TBulletProps = {
  /**
   * Ref to the DOM node.
   */
  ref?: ForwardedRef<View>
  /**
   * The content of the badge. The badge will be rendered relative to its children.
   */
  children?: string | number | ReactNode
  className?: string

  testID?: string
  /**
   * Classname or List of classes to change the classNames of the element.
   * if `className` is passed, it will be added to the base slot.
   *
   * @example
   * ```ts
   * <Badge classNames={{
   *    base:"base-classes", // wrapper
   *    badge: "badge-classes",
   * }} />
   * ```
   */
  classNames?: SlotsToClasses<TBulletSlots>
} & TBulletVariantProps

export const Bullet = ({ ref, ...props }: TBulletProps) => {
  const [enhancedProps, variantProps] = mapPropsVariants(props, bulletVariants.variantKeys)

  const { children, classNames, className, ...otherProps } = enhancedProps

  const isOneChar = typeof children === 'object' || String(children)?.length === 1 || props?.isOneChar

  const isDot = !children || props.isDot

  const baseStyles = clsx(classNames?.base, className)

  const slots = bulletVariants({ ...variantProps, isOneChar, isDot })

  const getBulletProps = () => ({
    className: slots.base({ class: baseStyles }),
    dataSet: {
      invisible: props?.isInvisible,
    },
    ...otherProps,
  })

  const getBulletTextProps = () => ({
    className: slots.text({ class: classNames?.text }),
  })

  const enhancedChildren = isValidElement(children) ? (
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- narrowing cloned child props
    cloneElement(children as ReactElement<{ className: string }>, {
      className: getBulletTextProps().className,
    })
  ) : (
    <Typography {...getBulletTextProps()}>{children}</Typography>
  )

  return (
    <View ref={ref} {...getBulletProps()}>
      {enhancedChildren}
    </View>
  )
}
